import { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { getBillings, Transaction } from "@/services/transactionsApi";
import { useAuth } from "@/context/AuthContext";
import ActionMenu from "@/components/billing/ActionMenu";
import DatePicker from "@/components/ui/DatePickerRange";
import DynamicPagination from "@/components/ui/dynamicPagination";

let searchDebounce: NodeJS.Timeout;

const BillingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id;

  const [data, setData] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);

  const skipInitialDate = useRef(true);
  const [userSelectedDate, setUserSelectedDate] = useState(false);

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const filterUserId = query.get("user_id") || null;

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Search
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Status filter
  const [status, setStatus] = useState<number | null>(null);

  // Date Range
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  // Debounce Search
  useEffect(() => {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 800);

    return () => clearTimeout(searchDebounce);
  }, [search]);

  // Auto clear input placeholder
  useEffect(() => {
    if (!userSelectedDate) {
      setTimeout(() => {
        const input = document.querySelector(
          ".date-ranger",
        ) as HTMLInputElement;
        if (input) input.value = "";
      }, 50);
    }
  }, [status, page, pageSize, debouncedSearch, userSelectedDate]);

  // Fetch API
  useEffect(() => {
    loadTransactions();
  }, [page, pageSize, debouncedSearch, status, startDate, endDate]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const res = await getBillings(
        userId,
        page,
        pageSize,
        debouncedSearch,
        status,
        startDate,
        endDate,
      );

      setData(
        res.value.map((t) => ({
          ...t,
          id: t._id,
        })),
      );

      setTotalRecords(res.pagination.total);
    } catch (error) {
      console.error("Failed to fetch transactions", error);
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Columns Configuration
  const columns = [
    { label: "Transaction ID", key: "payment_id" },

    {
      label: "Full Name",
      key: "user_id",
      render: (row) =>
        `${row.user_id?.f_name || ""} ${row.user_id?.l_name || ""}`.trim(),
    },

    {
      label: "Plan",
      key: "plan_id",
      render: (row) => row.subscription_id?.plan_details?.name || "-",
    },

    {
      label: "Total",
      key: "total",
      render: (row) => `₹${row.total}`,
    },

    {
      label: "Transaction Date & Time",
      key: "date",
      render: (row) => {
        const dateObj = new Date(row.date);
        return `${dateObj.toLocaleDateString("en-IN")} ${row.time}`;
      },
    },

    {
      label: "Status",
      key: "payment_status",
      render: (row) => {
        const s = row.payment_status;
        const statusText =
          s === 1
            ? "Success"
            : s === 2
              ? "Failed"
              : s === 4
                ? "Refund"
                : "Pending";
        const statusClass =
          s === 1
            ? "success color-success"
            : s === 2
              ? "danger color-danger"
              : s === 3
                ? "info color-info"
                : s === 4
                  ? "warning color-warning"
                  : "warning color-warning";

        return (
          <span
            className={`bg-opacity-${statusClass} rounded-pill userDatatable-content-status`}
          >
            {statusText}
          </span>
        );
      },
    },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const handleDateRange = (rangeString: string) => {
    if (skipInitialDate.current) {
      skipInitialDate.current = false;
      return;
    }

    const [start, end] = rangeString.split(",");

    setUserSelectedDate(true);
    setStartDate(start || null);
    setEndDate(end || null);

    setPage(1);
  };

  return (
    <div>
      <div className="container-fluid">
        <div className="social-dash-wrap">
          {/* Header */}
          <div className="row">
            <div className="col-lg-12">
              <div className="breadcrumb-main justify-content-lg-start card align-content-start p-2">
                <div className="align-content-center align-items-center card-body d-flex justify-content-between p-0 p-2 w-100">
                  <div className="">
                    <h3 className="text-xl mt-0.5 fw-600">Billings</h3>
                  </div>

                  <div className="breadcrumb-action justify-content-center flex-wrap">
                    <div className="action-btn">
                      <div className="form-group mb-0">
                        <div className="input-container icon-left position-relative border rounded">
                          <span className="input-icon icon-left"></span>

                          <DatePicker
                            placeholder="Select Date"
                            onDateChange={handleDateRange}
                          />

                          <span className="input-icon icon-right"></span>
                        </div>
                      </div>
                    </div>

                    <div className="form-group atbd-select d-flex align-items-center adv-table-searchs__status mb-0 mr-sm-30 mr-0">
                      <label className="d-flex align-items-center mb-sm-0 mb-2 ml-4">
                        Status
                      </label>

                      <select
                        className="form-control ml-sm-10 ml-0 border"
                        style={{ width: "8rem" }}
                        onChange={(e) => {
                          const v = e.target.value;
                          setStatus(v === "" ? null : Number(v));
                          setPage(1);
                        }}
                      >
                        <option value="">All</option>
                        <option value="1">Success</option>
                        <option value="2">Failed</option>
                        <option value="3">Pending</option>
                        <option value="4">Refund</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="row">
            <div className="col-lg-12">
              <div className="breadcrumb-main user-member justify-content-sm-between">
                
                <h4 className="text-capitalize breadcrumb-title">Billings</h4> 
                
                <div className="breadcrumb-action justify-content-center flex-wrap">
               
                  <div className="action-btn">
                    <div className="form-group mb-0">
                      <div className="input-container icon-left position-relative">
                        <span className="input-icon icon-left"></span>

                        <DatePicker
                          placeholder="Select Date"
                          onDateChange={handleDateRange}
                        />

                        <span className="input-icon icon-right"></span>
                      </div>
                    </div>
                  </div>

                   
                  <div className="form-group atbd-select d-flex align-items-center adv-table-searchs__status mb-0 mr-sm-30 mr-0">
                    <label className="d-flex align-items-center mb-sm-0 mb-2 ml-4">
                      Status
                    </label>

                    <select
                      className="form-control ml-sm-10 ml-0"
                      style={{ width: "8rem" }}
                      onChange={(e) => {
                        const v = e.target.value;
                        setStatus(v === "" ? null : Number(v));
                        setPage(1);
                      }}
                    >
                      <option value="">All</option>
                      <option value="1">Success</option>
                      <option value="2">Failed</option>
                      <option value="3">Pending</option>
                      <option value="4">Refund</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Table */}
          <div className="row">
            <div className="col-lg-12">
              <div className="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
                <div className="table-responsive" style={{ height: "506px" }}>
                  <table className="table mb-0 table-borderless">
                    <thead>
                      <tr>
                        {columns.map((col, idx) => (
                          <th key={idx}>{col.label}</th>
                        ))}
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <tr>
                          <td
                            colSpan={columns.length + 1}
                            className="text-center py-5"
                          >
                            <div className="spin-container d-flex justify-content-center">
                              <div className="atbd-spin-dots spin-sm">
                                <span className="spin-dot badge-dot dot-primary"></span>
                                <span className="spin-dot badge-dot dot-primary"></span>
                                <span className="spin-dot badge-dot dot-primary"></span>
                                <span className="spin-dot badge-dot dot-primary"></span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : data.length === 0 ? (
                        <tr>
                          <td
                            colSpan={columns.length + 1}
                            className="text-center py-3"
                          >
                            No transactions found
                          </td>
                        </tr>
                      ) : (
                        data.map((row, index) => (
                          <tr key={index}>
                            {columns.map((col, cIdx) => (
                              <td key={cIdx}>
                                {col.render ? col.render(row) : row[col.key]}
                              </td>
                            ))}

                            {/* ACTION MENU */}
                            <td>
                              {row["payment_status"] == 1 && (
                                <ActionMenu
                                  options={[
                                    {
                                      label: "View Invoice",
                                      icon: <FileText size={16} />,
                                      onClick: () =>
                                        navigate(
                                          `/billing/invoice-details/${row._id}`,
                                        ),
                                    },
                                  ]}
                                />
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <DynamicPagination
                    page={page}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageChange={(p) => setPage(p)}
                    onPageSizeChange={(s) => {
                      setPageSize(s);
                      setPage(1);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
